import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Mic } from 'lucide-react';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PageHero from '../../components/UI/PageHero';
import LoadingOverlay from '../../components/UI/LoadingOverlay';
import { interviewApi, interviewerApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { isInterviewer } from '../../utils/roles';
import { useMyContributorApplications } from '../../hooks/useMyContributorApplications';
import ContributorApplicationLocked from '../../components/contribute/ContributorApplicationLocked';
import { INTERVIEW_TECHNOLOGIES } from '../../content/interviewsPage';
import { AVAILABILITY_CHOICES } from '../../content/availability';
import { confirmAction } from '../../utils/confirmAction';
import { Link } from 'react-router-dom';

const interviewerSchema = z.object({
  fullName: z.string().min(2, 'Full name is required'),
  contactEmail: z.string().email('Please enter a valid email'),
  phoneNumber: z
    .string()
    .min(7, 'Please enter a valid phone number')
    .max(20, 'Phone number is too long'),
  currentJobTitle: z.string().min(2, 'Current job / title is required'),
  yearsOfExperience: z
    .number({ invalid_type_error: 'Years of experience is required' })
    .min(0, 'Enter a valid number')
    .max(60, 'Enter a valid number'),
  technologies: z.array(z.string()).min(1, 'Select at least one technology'),
  technicalSkills: z.string().min(2, 'Technical skills are required'),
  linkedInUrl: z
    .string()
    .optional()
    .refine(
      (value) => !value || /^https?:\/\/.+/i.test(value),
      'Please enter a valid LinkedIn URL'
    ),
  availability: z.array(z.string()).min(1, 'Select at least one availability option'),
});

type InterviewerFormData = z.infer<typeof interviewerSchema>;

const fieldClasses = `block w-full rounded-md border-2 border-gray-300 shadow-sm bg-white text-gray-900
  placeholder:text-gray-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20
  px-4 py-2.5 text-base transition duration-200 ease-in-out`;
const labelClasses = 'block text-sm font-semibold text-gray-900 mb-1.5';
const errorClasses = 'mt-1.5 text-sm text-error-600 font-medium';

const InterviewerRegistration: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [technologies, setTechnologies] = useState<string[]>([...INTERVIEW_TECHNOLOGIES]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { interviewer: interviewerApplication, canApplyInterviewer, loaded } = useMyContributorApplications();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<InterviewerFormData>({
    resolver: zodResolver(interviewerSchema),
    defaultValues: {
      technologies: [],
      availability: [],
      contactEmail: user?.email || '',
      phoneNumber: user?.mobile || '',
      linkedInUrl: user?.linkedInUrl || '',
      fullName: user?.username || '',
    },
  });

  useEffect(() => {
    if (user?.email) setValue('contactEmail', user.email);
    if (user?.mobile) setValue('phoneNumber', user.mobile);
    if (user?.linkedInUrl) setValue('linkedInUrl', user.linkedInUrl);
    if (user?.username) setValue('fullName', user.username);
  }, [user?.email, user?.mobile, user?.linkedInUrl, user?.username, setValue]);

  const selectedTechnologies = watch('technologies') || [];

  useEffect(() => {
    interviewApi.getConfig()
      .then((response) => {
        const fromApi = response.data.technologies?.filter((tech) => tech?.trim()) || [];
        if (fromApi.length) {
          setTechnologies(fromApi);
        }
      })
      .catch(() => undefined);
  }, []);

  const toggleTechnology = (tech: string) => {
    const next = selectedTechnologies.includes(tech)
      ? selectedTechnologies.filter((item) => item !== tech)
      : [...selectedTechnologies, tech];
    setValue('technologies', next, { shouldValidate: true });
  };

  const onSubmit = async (data: InterviewerFormData) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await interviewerApi.register({
        fullName: data.fullName,
        contactEmail: data.contactEmail,
        phoneNumber: data.phoneNumber,
        currentJobTitle: data.currentJobTitle,
        yearsOfExperience: data.yearsOfExperience,
        technologies: data.technologies,
        technicalSkills: data.technicalSkills,
        linkedInUrl: data.linkedInUrl,
        availability: data.availability.join(', '),
      });

      if (response.data?.success) {
        navigate('/dashboard', {
          state: { message: response.data.message || 'Application submitted for admin approval.' },
        });
      } else {
        setError(response.data?.message || 'Registration failed. Please try again.');
      }
    } catch (err: unknown) {
      const e = err as Error;
      setError(e.message || 'An error occurred during registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!loaded) {
    return (
      <div className="min-h-[calc(100dvh-4.5rem)]">
        <PageHero
          tone="interviews"
          eyebrow="Interview with us"
          icon={Mic}
          title="Join as an Interviewer"
          subtitle="Help candidates get interview-ready with realistic technical sessions in your stack."
        />
        <div className="py-16"><LoadingOverlay /></div>
      </div>
    );
  }

  if (isInterviewer(user) || (interviewerApplication?.hasApplication && !canApplyInterviewer)) {
    return (
      <div className="min-h-[calc(100dvh-4.5rem)]">
        <PageHero
          tone="interviews"
          eyebrow="Interview with us"
          icon={Mic}
          title="Join as an Interviewer"
          subtitle="Help candidates get interview-ready with realistic technical sessions in your stack."
        />
        {interviewerApplication?.hasApplication ? (
          <ContributorApplicationLocked role="interviewer" application={interviewerApplication} />
        ) : (
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <p className="text-lg font-semibold text-gray-900">You are already registered as an interviewer.</p>
          <p className="mt-2 text-sm text-gray-600">Assigned sessions are on your dashboard.</p>
          <Link to="/dashboard?tab=interviews" className="mt-6 inline-flex">
            <Button>Go to dashboard</Button>
          </Link>
        </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100dvh-4.5rem)]">
      <PageHero
        tone="interviews"
        eyebrow="Interview with us"
        icon={Mic}
        title="Join as an Interviewer"
        subtitle="Help candidates get interview-ready with realistic technical sessions in your stack."
      />

      <div className="sm:mx-auto sm:w-full sm:max-w-3xl px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="p-6 sm:p-8 relative">
            {isLoading && <LoadingOverlay />}

            {error && (
              <div className="mb-6 p-3 bg-error-50 text-error-600 text-sm rounded-md border border-error-200">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                <Input
                  id="fullName"
                  label="Full Name *"
                  placeholder="Enter your full name"
                  error={errors.fullName?.message}
                  {...register('fullName')}
                />
                <Input
                  id="contactEmail"
                  type="email"
                  label="Contact Email *"
                  disabled
                  error={errors.contactEmail?.message}
                  {...register('contactEmail')}
                />
                <Input
                  id="phoneNumber"
                  type="tel"
                  label="Phone Number *"
                  placeholder="Enter your phone number"
                  error={errors.phoneNumber?.message}
                  {...register('phoneNumber')}
                />
                <Input
                  id="currentJobTitle"
                  label="Current Job / Title *"
                  placeholder="e.g. Senior Software Engineer"
                  error={errors.currentJobTitle?.message}
                  {...register('currentJobTitle')}
                />
                <Input
                  id="yearsOfExperience"
                  type="number"
                  label="Years of Experience *"
                  placeholder="e.g. 8"
                  error={errors.yearsOfExperience?.message}
                  {...register('yearsOfExperience', { valueAsNumber: true })}
                />
                <Input
                  id="linkedInUrl"
                  label="LinkedIn URL"
                  placeholder="https://www.linkedin.com/in/..."
                  error={errors.linkedInUrl?.message}
                  {...register('linkedInUrl')}
                />
              </div>

              <div className="mb-4">
                <p className={labelClasses}>Technologies you can interview in *</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {technologies.map((tech) => (
                    <label
                      key={tech}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${
                        selectedTechnologies.includes(tech)
                          ? 'border-primary-600 bg-primary-50 text-primary-800'
                          : 'border-gray-300 bg-white text-gray-800'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={selectedTechnologies.includes(tech)}
                        onChange={() => toggleTechnology(tech)}
                      />
                      {tech}
                    </label>
                  ))}
                </div>
                {errors.technologies && (
                  <p className={errorClasses}>{errors.technologies.message}</p>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="technicalSkills" className={labelClasses}>
                  Technical Skills *
                </label>
                <textarea
                  id="technicalSkills"
                  rows={3}
                  className={fieldClasses}
                  placeholder="e.g. Java, Spring Boot, system design, coding interviews..."
                  {...register('technicalSkills')}
                />
                {errors.technicalSkills && (
                  <p className={errorClasses}>{errors.technicalSkills.message}</p>
                )}
              </div>

              <div className="mb-4">
                <p className={labelClasses}>Availability *</p>
                <p className="text-xs text-gray-500 mb-2">Select every option that fits. You can choose more than one.</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {AVAILABILITY_CHOICES.map((opt) => (
                    <label key={opt} className="flex items-center rounded-md border border-gray-200 px-3 py-2">
                      <input
                        type="checkbox"
                        value={opt}
                        {...register('availability')}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-800">{opt}</span>
                    </label>
                  ))}
                </div>
                {errors.availability && (
                  <p className={errorClasses}>{errors.availability.message}</p>
                )}
              </div>

              <div className="pt-4">
                <Button type="submit" fullWidth isLoading={isLoading}>
                  Submit Application
                </Button>
                <button
                  type="button"
                  className="mt-3 w-full text-sm font-medium text-gray-600 hover:text-gray-900"
                  onClick={async () => {
                    const ok = await confirmAction({
                      title: 'Discard this application?',
                      description: 'Your answers will not be saved.',
                      confirmLabel: 'Discard',
                      cancelLabel: 'Keep editing',
                      variant: 'danger',
                    });
                    if (!ok) return;
                    navigate('/');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default InterviewerRegistration;
